import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../environments/environment';
import { Observable } from 'rxjs';
import { SurveyReviewerSession, SurveyReviewerSessionFile, SurveyReviewerChatMessage, SurveyReviewerChatResponse, DecipherSurveyListItem, DecipherKeyOption, SurveyBuildImage, ProjectAssetsResult } from '../models/survey-reviewer';

@Injectable({
  providedIn: 'root'
})
export class SurveyReviewerService {

  private baseUrl = environment.apiEndpoint + '/surveyreviewer/';

  constructor(private http: HttpClient) { }

  // `asClient` asks for the client side (the "View client portal" preview). The server only lets
  // it narrow what an internal user gets, never widen what a client gets.
  GetActiveSession(projectId: string, asClient = false): Observable<SurveyReviewerSession> {
    return this.http.get<SurveyReviewerSession>(this.baseUrl + projectId + '/sessions/active', { params: this.audienceParams(asClient) });
  }

  GetFilesByProjectId(projectId: string): Observable<SurveyReviewerSessionFile[]> {
    return this.http.get<SurveyReviewerSessionFile[]>(this.baseUrl + projectId + '/files');
  }

  StartSession(projectId: string, formData: FormData): Observable<SurveyReviewerSession> {
    return this.http.post<SurveyReviewerSession>(this.baseUrl + projectId + '/sessions/start', formData);
  }

  GetFile(projectId: string, fileId = ''): Observable<any> {
    return this.http.get(this.baseUrl + projectId + '/export?fileId=' + fileId, { responseType: 'text' });
  }

  ApproveDocument(sessionId: string): Observable<SurveyReviewerSession> {
    return this.http.post<SurveyReviewerSession>(this.baseUrl + sessionId + '/approve', {});
  }

  UpdateStatus(session: SurveyReviewerSession, status: string): Observable<any> {
    const { files, ...sessionData } = session;
    const payload = { ...sessionData, status };
    return this.http.put(this.baseUrl + session.sessionId + '/status', payload);
  }

  GetArchivedSessions(projectId: string, asClient = false): Observable<SurveyReviewerSession[]> {
    return this.http.get<SurveyReviewerSession[]>(this.baseUrl + projectId + '/sessions/archived', { params: this.audienceParams(asClient) });
  }

  Chat(session: SurveyReviewerSession, inputText: string, asClient = false): Observable<SurveyReviewerChatResponse> {
    const { files, ...sessionData } = session;
    const payload = { ...sessionData, inputText };
    return this.http.put<SurveyReviewerChatResponse>(this.baseUrl + session.sessionId + '/chat', payload, { params: this.audienceParams(asClient) });
  }

  GetChatHistory(sessionId: string, asClient = false): Observable<SurveyReviewerChatMessage[]> {
    return this.http.get<SurveyReviewerChatMessage[]>(this.baseUrl + sessionId + '/chat/history', { params: this.audienceParams(asClient) });
  }

  private audienceParams(asClient: boolean): HttpParams {
    return asClient ? new HttpParams().set('audience', 'client') : new HttpParams();
  }

  AddSupportingDocuments(sessionId: string, formData: FormData): Observable<SurveyReviewerSession> {
    return this.http.post<SurveyReviewerSession>(this.baseUrl + sessionId + '/files', formData);
  }

  // The instance's Decipher API keys (one per Decipher account) a survey can be built in.
  GetDecipherKeys(projectId: string): Observable<DecipherKeyOption[]> {
    return this.http.get<DecipherKeyOption[]>(this.baseUrl + projectId + '/decipher/keys');
  }

  // keyId picks the Decipher account to search; null uses the default key.
  ListDecipherSurveys(projectId: string, search: string, keyId: string | null): Observable<DecipherSurveyListItem[]> {
    let params = new HttpParams().set('search', search || '');
    if (keyId) params = params.set('keyId', keyId);
    return this.http.get<DecipherSurveyListItem[]>(this.baseUrl + projectId + '/decipher/surveys', { params });
  }

  // The instance's designated "template" surveys across all its Decipher accounts, each with
  // its keyId — the picker shows only these when sending to Decipher without the "Copy?" option.
  GetDecipherTemplates(projectId: string): Observable<DecipherSurveyListItem[]> {
    return this.http.get<DecipherSurveyListItem[]>(this.baseUrl + projectId + '/decipher/templates');
  }

  // Replace the instance's designated template set, all accounts at once (from the "Manage
  // templates" modal).
  SaveDecipherTemplates(projectId: string, templates: DecipherSurveyListItem[]): Observable<any> {
    return this.http.put(this.baseUrl + projectId + '/decipher/templates', templates);
  }

  // `resolveLogic` asks the build to write the questionnaire's routing logic into the survey XML
  // as real Decipher conditions; off leaves the placeholder conditions for the programmer.
  // Ignored when copyOnly is true (a clone runs no build). `decipherKeyId` is the Decipher
  // account to create the survey in; null uses the instance's default key.
  StartBuildXml(projectId: string, sessionId: string, body: { templateSurveyPath: string; newName: string; copyOnly: boolean; resolveLogic: boolean; decipherKeyId: string | null }): Observable<any> {
    return this.http.post(this.baseUrl + projectId + '/sessions/' + sessionId + '/buildxml', body);
  }

  // Resume a failed build.
  //   no flags        re-push the already-built XML (transient failures)
  //   relint=true     re-run the agent's deterministic XML lint over the persisted fragments
  //                   and re-push — the cheap retry (no LLM) after Decipher rejected the survey
  //                   on schema grounds and an xml_lint rule has since been added
  //   regenerate=true re-run the agent for fresh XML (wins over relint)
  RetryBuildXml(projectId: string, sessionId: string, regenerate = false, relint = false): Observable<any> {
    const params = new HttpParams().set('regenerate', regenerate).set('relint', relint);
    return this.http.post(this.baseUrl + projectId + '/sessions/' + sessionId + '/buildxml/retry', {}, { params });
  }

  // Download the assembled survey XML for a failed build so it can be imported into Decipher
  // manually (the escape hatch for XML the automated push rejected and the AI can't fix).
  DownloadBuildXml(projectId: string, sessionId: string): Observable<Blob> {
    return this.http.get(this.baseUrl + projectId + '/sessions/' + sessionId + '/buildxml/download', { responseType: 'blob' });
  }

  // The project's image assets, carried into every survey a send creates.
  GetProjectAssets(projectId: string): Observable<SurveyBuildImage[]> {
    return this.http.get<SurveyBuildImage[]>(this.baseUrl + projectId + '/assets');
  }

  // Add images to the project's assets. With a sessionId whose survey already exists, the
  // backend also uploads them to it and declares each as a <res> in its survey.xml.
  UploadProjectAssets(projectId: string, sessionId: string | null, files: File[]): Observable<ProjectAssetsResult> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file, file.name));
    let params = new HttpParams();
    if (sessionId) params = params.set('sessionId', sessionId);
    return this.http.post<ProjectAssetsResult>(this.baseUrl + projectId + '/assets', formData, { params });
  }

  // Stop carrying an asset into future surveys; surveys that already have it keep it.
  DeleteProjectAsset(projectId: string, fileName: string): Observable<SurveyBuildImage[]> {
    return this.http.delete<SurveyBuildImage[]>(this.baseUrl + projectId + '/assets/' + encodeURIComponent(fileName));
  }

}
